'use client';

import React from 'react';
import Link from 'next/link';
import { Slide, SliderProvider, SliderTrack } from '@faceless-ui/slider';
import styles from './styles.module.scss';
import { Container } from '../../../../../../components/layouts';
import { ArticleCard, Button, Icon } from '../../../../../../components';
import colors from '../../../../../../styles/colors/index.module.scss';
import type { ArticlesQuery, CategoriesQuery } from '../../../../../../graphql/generated/schema';

interface Props {
  featuredCategory?: NonNullable<Extract<CategoriesQuery['Categories'], { __typename?: 'Categories' }>['docs']>[0];
  featuredArticles: ArticlesQuery['Articles'];
}

export const FeaturedArticles: React.FC<Props> = ({ featuredCategory, featuredArticles }) => {
  if (!featuredCategory) return null;

  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

  return (
    <div className={styles.container}>
      <SliderProvider
        slidesToShow={2}
        // 1920 is maxContentWidth
        // 80 is base(4)
        autoPlay
        autoplaySpeed={5000}
        scrollOffset={((windowWidth - 1920) / 2) + (80 * 2) + 10}
      >
        {({ goToNextSlide, goToPrevSlide, currentSlideIndex }) => (
          <React.Fragment>
            <Container className={styles.headerContainer}>
              <div>
                <div className={styles.titleContainer}>
                  <Icon
                    icon="boltClassicLight"
                    fill={colors.secondary}
                    size={38}
                  />
                  <h2 className={styles.title}>{featuredCategory.title}</h2>
                  <Button
                    htmlElement="span"
                    className={styles.tag}
                  >
                    Featured Resources
                  </Button>
                </div>
                <p>{featuredCategory.description}</p>
                <Link
                  className={styles.link}
                  href={`/articles/${featuredCategory.slug}`}
                >
                  View All
                </Link>
              </div>
              <div>
                <Button
                  type="tertiary"
                  icon="circleChevronLeft"
                  iconSize={40}
                  iconFill={colors.secondary}
                  onClick={goToPrevSlide}
                  disabled={currentSlideIndex === 0}
                />
                <Button
                  type="tertiary"
                  icon="circleChevronRight"
                  iconSize={40}
                  iconFill={colors.secondary}
                  onClick={goToNextSlide}
                  disabled={currentSlideIndex === (featuredArticles?.docs?.length || 0) - 2}
                />
              </div>
            </Container>
            <div className={styles.sliderContianer}>
              <SliderTrack className={styles.sliderContainer}>
                {featuredArticles?.docs?.map((article, index) => (
                  <Slide
                    index={index}
                    key={article?.id}
                  >
                    <div className={styles.slideCardContainer}>
                      <ArticleCard
                        article={article}
                        largeCard
                        priority={index <= 2}
                      />
                    </div>
                  </Slide>
                ))}
              </SliderTrack>
            </div>
          </React.Fragment>
        )}
      </SliderProvider>
    </div>
  );
};
